import React, { useState, useEffect } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, TextField, Button, Tooltip, Snackbar, Alert, CircularProgress } from '@mui/material';
import { PhoneInput } from 'react-international-phone';
import 'react-international-phone/style.css';
import axios from '../utils/axiosConfig';

const ParticipantDialog = ({ open, onClose, interviewId, projectId, isNewParticipant, onUpdate }) => {
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [errorOpen, setErrorOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [agentSpecificMetadata, setAgentSpecificMetadata] = useState('');

  useEffect(() => {
    if (!isNewParticipant && interviewId) {
      const fetchParticipant = async () => {
        try {
          const response = await axios.get(`/api/interviews/${interviewId}`);
          const interview = response.data;
          setName(interview.participant_name);
          setPhone(interview.participant_phone);
          setAgentSpecificMetadata(JSON.stringify(interview.agent_specific_metadata, null, 2));
        } catch (error) {
          console.error('Failed to fetch participant:', error);
        }
      };

      fetchParticipant();
    }
  }, [interviewId, projectId, isNewParticipant]);

  const handleSave = async () => {
    setLoading(true);
    let parsedMetadata;
    if (agentSpecificMetadata) {
      try {
        parsedMetadata = JSON.parse(agentSpecificMetadata);
      } catch (error) {
        setErrorMessage('Invalid JSON format for Agent Specific Metadata.');
        setErrorOpen(true);
        setLoading(false);
        return;
      }
    }

    const participantData = {
      participant_name: name,
      participant_phone: phone,
      agent_specific_metadata: JSON.stringify(parsedMetadata),
    };


    try {
      if (isNewParticipant) {
        await axios.post(`/api/projects/${projectId}/interview`, participantData);
      } else {
        await axios.put(`/api/interviews/${interviewId}`, participantData);
      }
      onUpdate();
      onClose();
    } catch (error) {
      console.error('Failed to save participant:', error);
      setErrorMessage('Failed to save participant. Please try again.');
      setErrorOpen(true);
    } finally {
      setLoading(false);
    }
  };

  const handleCloseError = () => {
    setErrorOpen(false);
  };

  const phoneInputStyles = {
    dropdown: {
      zIndex: 1000, // Ensure the dropdown is above other elements
    },
  };

  return (
    <>
      <style>
        {`
          .react-international-phone-country-selector-dropdown {
            z-index: 1000 !important; /* Ensure the dropdown is above other elements */
          }
        `}
      </style>
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitle>{isNewParticipant ? 'Add New Participant' : 'Edit Participant'}</DialogTitle>
        <DialogContent dividers style={{ maxHeight: '70vh', overflow: 'auto' }}>
          <Tooltip title="Enter the name of the participant">
            <TextField
              label="Name"
              fullWidth
              margin="normal"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
            />
          </Tooltip>
          <Tooltip title="Enter the phone number of the participant">
            <PhoneInput
              defaultCountry="US"
              value={phone}
              onChange={(value) => setPhone(value)}
            />
          </Tooltip>
          <Tooltip title="Additional metadata about the participant to be supplied to the interviewer agent, in JSON format">
            <TextField
              label="Agent Specific Metadata"
              fullWidth
              margin="normal"
              multiline
              minRows={4}
              value={agentSpecificMetadata}
              onChange={(e) => setAgentSpecificMetadata(e.target.value)}
            />
          </Tooltip>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => onClose()} color="primary" disabled={loading}>
            Cancel
          </Button>
          <Button onClick={handleSave} color="primary" variant="contained" disabled={loading}>
            {loading ? <CircularProgress size={24} /> : (isNewParticipant ? 'Add' : 'Save')}
          </Button>
        </DialogActions>
      </Dialog>
      <Snackbar open={errorOpen} autoHideDuration={6000} onClose={handleCloseError}>
        <Alert onClose={handleCloseError} severity="error" sx={{ width: '100%' }}>
          {errorMessage}
        </Alert>
      </Snackbar>
    </>
  );
};

export default ParticipantDialog;